import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Users,
  Briefcase,
  FileText,
  DollarSign,
  TrendingUp,
  Crown,
  Zap,
  Building2,
  RefreshCw,
  Shield,
  Calendar,
  Mail,
  Activity,
  Lock,
  KeyRound,
  Edit,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [verifyingPin, setVerifyingPin] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [planFilter, setPlanFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  
  // Plan change modal state
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPlan, setNewPlan] = useState("");
  const [planChangeReason, setPlanChangeReason] = useState("");
  const [updatingPlan, setUpdatingPlan] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, [user?.email]);

  const checkAdminAccess = async () => {
    if (!user?.email) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/admin/check?email=${encodeURIComponent(user.email)}`);
      if (response.data.is_admin) {
        setIsAdmin(true);
        // Check if PIN was already verified this session
        const savedPin = sessionStorage.getItem("adminPinVerified");
        if (savedPin === user.email) {
          setPinVerified(true);
          await fetchAllData();
        }
      }
    } catch (error) {
      console.error("Error checking admin access:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (pin.length < 4) {
      setPinError("El PIN debe tener al menos 4 dígitos");
      return;
    }

    setVerifyingPin(true);
    setPinError("");

    try {
      await axios.post(`${API}/admin/verify-pin?email=${encodeURIComponent(user.email)}&pin=${pin}`);
      setPinVerified(true);
      sessionStorage.setItem("adminPinVerified", user.email);
      toast.success("Acceso concedido");
      await fetchAllData();
    } catch (error) {
      setPinError("PIN incorrecto");
      setPin("");
    } finally {
      setVerifyingPin(false);
    }
  };

  const fetchAllData = async () => {
    setRefreshing(true);
    try {
      const [dashboardRes, usersRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard?admin_email=${encodeURIComponent(user.email)}`),
        axios.get(`${API}/admin/users?admin_email=${encodeURIComponent(user.email)}&plan_filter=${planFilter}`),
        axios.get(`${API}/admin/payments?admin_email=${encodeURIComponent(user.email)}`),
      ]);
      setDashboard(dashboardRes.data);
      setUsers(usersRes.data.users);
      setPayments(paymentsRes.data.payments);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Error al cargar datos de administrador");
    } finally {
      setRefreshing(false);
    }
  };

  const openPlanModal = (userToEdit) => {
    setSelectedUser(userToEdit);
    setNewPlan(userToEdit.plan || "free");
    setPlanChangeReason("");
    setShowPlanModal(true);
  };

  const handlePlanChange = async () => {
    if (!selectedUser || !newPlan) return;
    
    setUpdatingPlan(true);
    try {
      await axios.post(
        `${API}/admin/users/update-plan?admin_email=${encodeURIComponent(user.email)}`,
        {
          user_email: selectedUser.email,
          new_plan: newPlan,
          reason: planChangeReason || "Actualización manual desde panel admin"
        }
      );
      toast.success(`Plan actualizado a ${newPlan.toUpperCase()} para ${selectedUser.email}`);
      setShowPlanModal(false);
      // Refresh users list
      const usersRes = await axios.get(`${API}/admin/users?admin_email=${encodeURIComponent(user.email)}&plan_filter=${planFilter}`);
      setUsers(usersRes.data.users);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar plan");
    } finally {
      setUpdatingPlan(false);
    }
  };

  const fetchUsers = async (filter) => {
    try {
      const response = await axios.get(`${API}/admin/users?admin_email=${encodeURIComponent(user.email)}&plan_filter=${filter}`);
      setUsers(response.data.users);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleFilterChange = (value) => {
    setPlanFilter(value);
    fetchUsers(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPlanBadge = (plan) => {
    const styles = {
      free: "bg-gray-100 text-gray-700",
      pro: "bg-yellow-100 text-yellow-700",
      business: "bg-purple-100 text-purple-700",
    };
    const icons = {
      free: Zap,
      pro: Crown,
      business: Building2,
    };
    const Icon = icons[plan] || Zap;
    return (
      <Badge className={`${styles[plan] || styles.free} gap-1`}>
        <Icon className="w-3 h-3" />
        {plan?.charAt(0).toUpperCase() + plan?.slice(1) || "Free"}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="space-y-6">
        <Card className="text-center py-12">
          <CardContent>
            <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold mb-2">Acceso Restringido</h2>
            <p className="text-muted-foreground mb-4">
              Debes iniciar sesión para acceder al panel de administrador.
            </p>
            <Button onClick={() => navigate("/")} className="rounded-full">
              Ir al Inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <Card className="text-center py-12">
          <CardContent>
            <Shield className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold mb-2">Acceso Denegado</h2>
            <p className="text-muted-foreground mb-4">
              No tienes permisos de administrador.
            </p>
            <Button onClick={() => navigate("/")} variant="outline" className="rounded-full">
              Volver al Inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // PIN verification screen
  if (!pinVerified) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="w-full max-w-sm">
          <CardContent className="pt-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-8 h-8 text-primary" />
              </div>
              <h2 className="font-heading text-xl font-bold">Panel de Admin</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Ingresa tu PIN de seguridad
              </p>
            </div>

            <form onSubmit={handlePinSubmit} className="space-y-4">
              <div>
                <Input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="••••"
                  value={pin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setPin(value);
                    setPinError("");
                  }}
                  className="text-center text-2xl tracking-widest"
                  autoFocus
                />
                {pinError && (
                  <p className="text-sm text-destructive mt-2 text-center">{pinError}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full rounded-full"
                disabled={pin.length < 4 || verifyingPin}
              >
                {verifyingPin ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Lock className="w-4 h-4 mr-2" />
                )}
                {verifyingPin ? "Verificando..." : "Acceder"}
              </Button>
            </form>

            <Button
              variant="ghost"
              className="w-full mt-4"
              onClick={() => navigate("/")}
            >
              Cancelar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
              <Shield className="w-7 h-7 text-primary" />
              Panel de Administrador
            </h1>
            <p className="text-muted-foreground mt-1">
              Estadísticas y gestión de la plataforma
            </p>
          </div>
        </div>
        <Button
          onClick={fetchAllData}
          variant="outline"
          className="gap-2"
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      {/* Overview Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{dashboard.overview.total_users}</p>
                  <p className="text-xs text-muted-foreground">Usuarios Totales</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{dashboard.overview.active_users_7d}</p>
                  <p className="text-xs text-muted-foreground">Activos (7 días)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{dashboard.overview.total_jobs}</p>
                  <p className="text-xs text-muted-foreground">Puestos Creados</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{dashboard.overview.total_candidates}</p>
                  <p className="text-xs text-muted-foreground">CVs Analizados</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Revenue & Plans */}
      {dashboard && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Ingresos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-sm">Ingresos Totales</span>
                <span className="text-xl font-heading font-bold text-green-600">
                  ${dashboard.revenue.total_revenue.toFixed(2)} USD
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                <span className="text-sm">Últimos 30 días</span>
                <span className="text-lg font-heading font-bold">
                  ${dashboard.revenue.recent_revenue_30d.toFixed(2)} USD
                </span>
              </div>
              <div className="flex justify-between items-center text-sm text-muted-foreground">
                <span>Pagos completados</span>
                <span>{dashboard.revenue.total_payments}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Distribución de Planes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-gray-600" />
                  <span>Free</span>
                </div>
                <span className="font-heading font-bold">{dashboard.users_by_plan.free}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Crown className="w-4 h-4 text-yellow-600" />
                  <span>Pro ($20/mes)</span>
                </div>
                <span className="font-heading font-bold">{dashboard.users_by_plan.pro}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-purple-600" />
                  <span>Business ($50/mes)</span>
                </div>
                <span className="font-heading font-bold">{dashboard.users_by_plan.business}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for Users and Payments */}
      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users" className="gap-2">
            <Users className="w-4 h-4" />
            Usuarios ({users.length})
          </TabsTrigger>
          <TabsTrigger value="payments" className="gap-2">
            <DollarSign className="w-4 h-4" />
            Pagos ({payments.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-4">
          {/* Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Filtrar por plan:</span>
            <Select value={planFilter} onValueChange={handleFilterChange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="business">Business</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Users Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead className="text-center">Puestos</TableHead>
                    <TableHead className="text-center">CVs</TableHead>
                    <TableHead>Registro</TableHead>
                    <TableHead>Última Actividad</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No hay usuarios registrados
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((u) => (
                      <TableRow key={u.email}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            {u.email}
                          </div>
                        </TableCell>
                        <TableCell>{getPlanBadge(u.plan)}</TableCell>
                        <TableCell className="text-center">{u.jobs_count || 0}</TableCell>
                        <TableCell className="text-center">{u.candidates_count || 0}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(u.created_at)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(u.last_active)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPlanModal(u)}
                            className="gap-1"
                          >
                            <Edit className="w-3 h-3" />
                            Cambiar Plan
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead>ID Transacción</TableHead>
                    <TableHead>Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No hay pagos registrados
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map((p, i) => (
                      <TableRow key={p.order_id || i}>
                        <TableCell className="font-medium">{p.user_email}</TableCell>
                        <TableCell>{getPlanBadge(p.plan_id)}</TableCell>
                        <TableCell className="text-right font-heading font-bold text-green-600">
                          ${p.amount?.toFixed(2)} USD
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {p.order_id?.substring(0, 20)}...
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(p.completed_at)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Plan Change Modal */}
      <Dialog open={showPlanModal} onOpenChange={setShowPlanModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              Cambiar Plan de Usuario
            </DialogTitle>
            <DialogDescription>
              {selectedUser && (
                <span>Usuario: <strong>{selectedUser.email}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Current Plan */}
            {selectedUser && (
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="text-sm">Plan actual:</span>
                {getPlanBadge(selectedUser.plan)}
              </div>
            )}

            {/* New Plan Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Nuevo plan:</label>
              <Select value={newPlan} onValueChange={setNewPlan}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      Free - Gratuito
                    </div>
                  </SelectItem>
                  <SelectItem value="pro">
                    <div className="flex items-center gap-2">
                      <Crown className="w-4 h-4 text-yellow-600" />
                      Pro - $20/mes
                    </div>
                  </SelectItem>
                  <SelectItem value="business">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-purple-600" />
                      Business - $50/mes
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Razón del cambio (opcional):</label>
              <Input
                placeholder="Ej: Prueba gratuita, promoción especial..."
                value={planChangeReason}
                onChange={(e) => setPlanChangeReason(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handlePlanChange}
              disabled={updatingPlan || newPlan === selectedUser?.plan}
              className="gap-2"
            >
              {updatingPlan ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              {updatingPlan ? "Actualizando..." : "Guardar Cambio"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
